import { useEffect, useRef, useState } from "react";
import api from "@/lib/apiClient";
import { ASK } from "@/constants/testIds";
import {
  Sparkles, Lightbulb, Layers, Dumbbell, ListChecks, Languages, Maximize2, X, Copy,
} from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [
  { key: "explain", label: "Explain", icon: Sparkles },
  { key: "simplify", label: "Simplify", icon: Lightbulb },
  { key: "example", label: "Example", icon: Layers },
  { key: "practice", label: "Practice", icon: Dumbbell },
  { key: "quiz", label: "Quiz me", icon: ListChecks },
  { key: "translate", label: "Translate", icon: Languages },
  { key: "expand", label: "Expand", icon: Maximize2 },
];

/**
 * Wraps children — any text selected within will show a floating "Ask your mentor" menu.
 * On action click, opens a right-side sheet with the mentor's response.
 */
export function AskAnywhereProvider({ children, contextLabel = "" }) {
  const [menu, setMenu] = useState(null); // { x, y, text }
  const [panel, setPanel] = useState(null); // { action, text, output, loading }
  const wrapRef = useRef(null);

  useEffect(() => {
    const onSelection = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (!text || !wrapRef.current || !sel.anchorNode) {
        setMenu(null);
        return;
      }
      const container = wrapRef.current;
      if (!container.contains(sel.anchorNode)) {
        setMenu(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      setMenu({
        x: rect.left + rect.width / 2 + window.scrollX,
        y: rect.top + window.scrollY - 12,
        text,
      });
    };
    document.addEventListener("mouseup", onSelection);
    document.addEventListener("keyup", onSelection);
    return () => {
      document.removeEventListener("mouseup", onSelection);
      document.removeEventListener("keyup", onSelection);
    };
  }, []);

  const run = async (action) => {
    if (!menu) return;
    const text = menu.text;
    setMenu(null);
    setPanel({ action, text, loading: true, output: "" });
    try {
      const body = { action, text, context: contextLabel };
      if (action === "translate") body.target_language = "Spanish";
      const { data } = await api.post("/ask", body);
      setPanel((p) => ({ ...p, loading: false, output: data.output }));
    } catch (e) {
      setPanel((p) => ({ ...p, loading: false, output: "Your mentor is temporarily unavailable." }));
    }
  };

  const copy = () => {
    if (!panel?.output) return;
    navigator.clipboard.writeText(panel.output);
    toast.success("Copied to clipboard");
  };

  return (
    <div ref={wrapRef} className="relative">
      {children}

      {/* Floating menu */}
      {menu && (
        <div
          data-testid={ASK.menu}
          className="fixed z-50 -translate-x-1/2 -translate-y-full rounded-full border border-primary/40 bg-background/95 backdrop-blur-xl px-1 py-1 shadow-[0_8px_24px_-4px_rgba(0,229,255,0.35)] animate-fade-up"
          style={{ left: menu.x, top: menu.y }}
        >
          <div className="flex items-center gap-1">
            <div className="px-2 label-mono text-primary">Ask mentor</div>
            {ACTIONS.map((a) => (
              <button
                key={a.key}
                data-testid={ASK.action(a.key)}
                onClick={() => run(a.key)}
                className="rounded-full p-1.5 hover:bg-primary/10 transition"
                title={a.label}
              >
                <a.icon className="h-3.5 w-3.5 text-primary" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Slide-in panel */}
      {panel && (
        <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-card shadow-2xl animate-fade-up">
          <div className="flex items-center justify-between border-b border-border p-4">
            <div>
              <div className="label-mono text-primary">// {panel.action}</div>
              <div className="mt-1 font-display font-bold">Ask your mentor</div>
            </div>
            <button data-testid={ASK.closeBtn} onClick={() => setPanel(null)} className="rounded-full border border-border p-2 hover:border-primary/50">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div data-testid={ASK.panel} className="flex-1 overflow-y-auto p-4">
            <div className="mb-4 rounded-lg border border-border bg-muted/50 p-3">
              <div className="label-mono">Selected</div>
              <div className="mt-1 text-xs italic">"{panel.text.slice(0, 220)}{panel.text.length > 220 ? "…" : ""}"</div>
            </div>
            {panel.loading ? (
              <div className="text-sm text-muted-foreground"><span className="chat-thinking">Thinking</span></div>
            ) : (
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{panel.output}</div>
            )}
          </div>
          {!panel.loading && panel.output && (
            <div className="border-t border-border p-3">
              <button
                onClick={copy}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 font-mono text-xs hover:border-primary"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
